/**
 * wc77-analytics.js — 3-leg analytics fan-out for the wc77 chain.
 * 2026-07-01 — tracking-layer spec §2.0 (event dictionary conventions) + §3.
 *
 * WHY A NEW MODULE (instead of editing wc2-analytics.js): wc2-analytics.js is
 * shared by every historical wcNN chain on the CDN, so its behavior is frozen.
 * The wc77 chain imports THIS module instead; wc2-analytics stays untouched
 * for the old chains. log()/trace() are re-exported unchanged — only track()
 * (and captureNetworkEvent, which calls it) change behavior.
 *
 * What track() does per event — the single choke point for the whole-flow
 * A/B/C split test's triple-redundant funnel:
 *
 *   0. stampEnvelope(): fills the common envelope ONCE here, not at ~50 call
 *      sites — event_id (same uuid on all legs = cross-source reconciliation
 *      + dedup key), flow_arm / sub_variant / wc_uid (from the head assigner,
 *      snippets/wolden-flow-assign.liquid), wolden_session_id, ph_distinct_id,
 *      generation_id, page_type, page, product_handle, qa_override.
 *      Caller-provided values always win — the envelope only fills gaps (so
 *      wolden_flow_exposed keeps the event_id its head beacon already sent).
 *   1. PostHog capture('wolden_'+name) — unchanged from wc2-analytics.
 *   2. GA4 via the direct gtag stub (snippets/wolden-ga4.liquid) —
 *      UNCONDITIONAL. The old fan-out gated on `typeof window.gtag ===
 *      'function'` and silently dropped every event fired before Littledata's
 *      async gtag.js landed; the head stub now guarantees a queue exists.
 *      De-dup rule vs Littledata: namespace separation — we ONLY emit
 *      wolden_*-prefixed custom events, never GA4 reserved/standard ecommerce
 *      names (page_view, view_item, add_to_cart, begin_checkout, purchase);
 *      Littledata remains the sole writer of standard ecommerce.
 *   3. First-party beacon (navigator.sendBeacon → collector Lambda →
 *      funnel_events) — the ad-blocker-proof leg. Reads the endpoint from
 *      window.WC_SETTINGS.wc_flow_beacon_url (theme setting); silently
 *      no-ops while that setting is blank. This leg (and ONLY this leg) also
 *      merges the visitor's first-touch utm_* + fbclid (WC_FLOW.attribution)
 *      into props{} so every firstparty row can join ad → campaign.
 *   4. Compat legs kept from wc2-analytics: the `wolden:track` DOM
 *      CustomEvent (wc77-experiments.js LISTENS on it — a live dependency)
 *      and the Klaviyo _learnq push (dead today, kept verbatim).
 *
 * Deliberately DROPPED from wc2-analytics: the raw GTM-shape
 * dataLayer.push({event:'wolden_*'}). The gtag stub already pushes into
 * dataLayer in gtag form; keeping both would double-emit if a GTM container
 * ever loads.
 *
 * Also exports window.WC.track: classic asset_url scripts (wc-atc-guard.js)
 * can't import ES modules. Before this, only the legacy monolith defined
 * WC.track, so on the live v2 chain the ATC guard's event fell through to a
 * raw UNPREFIXED posthog.capture — invisible to every wolden_* query.
 */

import { log, trace } from './wc2-analytics.js';
import { getSessionId } from './wc2-state.js';

// Re-export so wc77 modules need exactly one analytics import.
export { log, trace };

// ─── GA4 hygiene limits (spec §2.0) ───────────────────────────
// GA4 silently mangles events that break these; sanitize at the choke point.
var GA4_MAX_NAME = 40;
var GA4_MAX_PARAMS = 25;
var GA4_MAX_VALUE = 100;

function uuid() {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (e) { /* ignore */ }
  var s = '', x = '0123456789abcdef';
  for (var j = 0; j < 36; j++) s += (j === 8 || j === 13 || j === 18 || j === 23) ? '-' : x.charAt(Math.floor(Math.random() * 16));
  return s;
}

// ─── Envelope ─────────────────────────────────────────────────
// Fill-if-missing only: callers that already know a field (generation_id at
// upload time, the head assigner's event_id on flow_exposed) keep their value.
function stampEnvelope(properties) {
  var out = {};
  var p = properties || {};
  for (var k in p) {
    if (Object.prototype.hasOwnProperty.call(p, k)) out[k] = p[k];
  }

  var f = null;
  try { f = window.WC_FLOW || null; } catch (e) { /* ignore */ }

  if (out.event_id == null) out.event_id = uuid();
  if (out.flow_arm == null) out.flow_arm = (f && !f.error && f.arm) ? f.arm : null;
  if (out.sub_variant == null) out.sub_variant = (f && f.subVariant) ? f.subVariant : null;
  if (out.wc_uid == null) out.wc_uid = (f && f.uid) ? f.uid : null;
  // qa_override only when forced (spec §2.0) — absent on organic traffic.
  if (f && f.forced && out.qa_override == null) out.qa_override = true;

  if (out.wolden_session_id == null) {
    try { out.wolden_session_id = getSessionId() || null; } catch (e) { out.wolden_session_id = null; }
  }
  if (out.ph_distinct_id == null) {
    // Pre-hydration the PostHog stub queues the call and returns undefined —
    // normalize to null; later events get the real id.
    try {
      var pid = (window.posthog && typeof window.posthog.get_distinct_id === 'function')
        ? window.posthog.get_distinct_id() : null;
      out.ph_distinct_id = pid || null;
    } catch (e) { out.ph_distinct_id = null; }
  }
  if (out.generation_id == null) {
    // Null pre-upload by design (spec §2.0) — the active gen once one exists.
    try {
      out.generation_id = (window.WC && window.WC._currentResult && window.WC._currentResult.generationId) || null;
    } catch (e) { out.generation_id = null; }
  }
  if (out.page_type == null) {
    // Body classification (parent_pdp/child_pdp/my_generations) beats the
    // Liquid template name the head assigner captured.
    try {
      var bt = document.body && document.body.getAttribute('data-wc-page-type');
      out.page_type = bt || (f && f.exposure && f.exposure.page_type) || null;
    } catch (e) { out.page_type = null; }
  }
  if (out.page == null) {
    try { out.page = location.pathname; } catch (e) { /* ignore */ }
  }
  if (out.product_handle == null) {
    try {
      var m = location.pathname.match(/\/products\/([^/?#]+)/);
      out.product_handle = m ? m[1] : null;
    } catch (e) { /* ignore */ }
  }
  return out;
}

// ─── GA4 param sanitizer ──────────────────────────────────────
// ≤25 params, string values ≤100 chars, nulls dropped (GA4 rejects them).
// Numbers/booleans pass through so value fields stay aggregatable.
function ga4Sanitize(props) {
  var out = {}, count = 0;
  for (var k in props) {
    if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
    if (count >= GA4_MAX_PARAMS) break;
    var v = props[k];
    if (v == null) continue;
    if (typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    } else {
      if (typeof v !== 'string') {
        try { v = JSON.stringify(v); } catch (e) { v = String(v); }
      }
      out[k] = v.length > GA4_MAX_VALUE ? v.slice(0, GA4_MAX_VALUE) : v;
    }
    count++;
  }
  return out;
}

// Envelope keys sent FLAT on the beacon payload (collector contract, spec
// §3.3); everything else nests under props{}.
var BEACON_ENVELOPE_KEYS = {
  event_id: 1, flow_arm: 1, sub_variant: 1, wc_uid: 1, wolden_session_id: 1,
  ph_distinct_id: 1, generation_id: 1, page_type: 1, page: 1,
  product_handle: 1, qa_override: 1,
};

// ─── track() — the 3-leg fan-out ──────────────────────────────
export function track(eventName, properties) {
  var props = stampEnvelope(properties);
  log('track →', eventName, props);

  // Leg 1 — PostHog (auto-prefix wolden_; same as wc2-analytics).
  try {
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture('wolden_' + eventName, props);
    }
  } catch (e) { /* ignore */ }

  // Leg 2 — GA4 via the direct gtag stub. Unconditional: the head stub
  // (snippets/wolden-ga4.liquid) defines window.gtag synchronously, so
  // nothing is dropped pre-load. Name truncated to GA4's 40-char cap.
  try {
    var ga4Name = ('wolden_' + eventName).slice(0, GA4_MAX_NAME);
    var ga4Props = ga4Sanitize(props);
    if (typeof window.gtag === 'function') {
      window.gtag('event', ga4Name, ga4Props);
    } else {
      // Stub missing (snippet removed / failed): push in gtag's own
      // arguments form so a later gtag.js load still processes the event.
      window.dataLayer = window.dataLayer || [];
      (function () { window.dataLayer.push(arguments); })('event', ga4Name, ga4Props);
    }
  } catch (e) { /* ignore */ }

  // Leg 3 — first-party beacon → collector Lambda → funnel_events.
  // Fire-and-forget; inert until the theme setting holds a collector URL.
  try {
    var url = (window.WC_SETTINGS && window.WC_SETTINGS.wc_flow_beacon_url) || '';
    if (url) {
      var extra = {};
      for (var k in props) {
        if (Object.prototype.hasOwnProperty.call(props, k) && !BEACON_ENVELOPE_KEYS[k]) extra[k] = props[k];
      }
      // First-touch ad attribution (utm_* + fbclid — pinned in the wc_ab
      // record by the head assigner, exposed at window.WC_FLOW.attribution)
      // rides in props{} on this BEACON leg only. WHY props, not top-level:
      // the collector whitelists top-level envelope fields (flow-beacon
      // STRING_FIELDS) and drops unknown flat keys, but passes props{}
      // through whole; the drain (connector_firstparty._utm_fields) reads
      // props-flat keys into the funnel_events utm/fbclid columns.
      // Deliberately NOT stamped into the PostHog/GA4 legs: PostHog captures
      // utm params natively and GA4 derives source attribution from the URL —
      // duplicating them there would only burn GA4's 25-param budget.
      try {
        var fattr = (window.WC_FLOW && window.WC_FLOW.attribution) || null;
        if (fattr) {
          for (var ak in fattr) {
            if (Object.prototype.hasOwnProperty.call(fattr, ak) && extra[ak] == null) extra[ak] = fattr[ak];
          }
        }
      } catch (e) { /* attribution is best-effort — never block the beacon */ }
      var pl = JSON.stringify({
        event: 'wolden_' + eventName,
        event_id: props.event_id,
        ts: Date.now(),
        anonymous_id: props.wc_uid || null,
        session_id: props.wolden_session_id || null,
        ph_distinct_id: props.ph_distinct_id || null,
        flow_arm: props.flow_arm || null,
        sub_variant: props.sub_variant || null,
        generation_id: props.generation_id || null,
        page_type: props.page_type || null,
        page: props.page || null,
        product_handle: props.product_handle || null,
        qa_override: !!props.qa_override,
        props: extra,
      });
      var sent = false;
      // text/plain Blob → no CORS preflight (spec §3.3).
      try {
        if (navigator.sendBeacon) sent = navigator.sendBeacon(url, new Blob([pl], { type: 'text/plain' }));
      } catch (e) { /* ignore */ }
      if (!sent && window.fetch) {
        fetch(url, { method: 'POST', body: pl, keepalive: true })['catch'](function () {});
      }
    }
  } catch (e) { /* ignore */ }

  // Compat leg — Klaviyo client tracker (dead today; kept verbatim from
  // wc2-analytics so behavior is identical if onsite Klaviyo ever returns).
  try {
    if (window._learnq) {
      window._learnq.push(['track', 'Wolden ' + eventName, props]);
    }
  } catch (e) { /* ignore */ }

  // Compat leg — DOM CustomEvent. NOT optional: wc77-experiments.js listens
  // on wolden:track to re-render variant UI. Enveloped props are a superset
  // of what listeners saw before, so detail shape stays compatible.
  try {
    document.dispatchEvent(new CustomEvent('wolden:track', {
      bubbles: true, detail: { eventName: eventName, properties: props },
    }));
  } catch (e) { /* ignore */ }
}

// ─── Network diagnostic logging ───────────────────────────────
// Copied from wc2-analytics.js (not re-exported) because it must call THIS
// module's track() — the wc2 copy would route through the old fan-out and
// miss the envelope + beacon legs. Logic unchanged otherwise.
export function captureNetworkEvent(label, url, resp, body, error, startedAt, extra) {
  try {
    var u = null;
    try { u = new URL(url); } catch (_) { /* relative or invalid URL */ }
    var headers = {};
    if (resp && resp.headers && typeof resp.headers.get === 'function') {
      var keys = [
        'cf-mitigated', 'cf-ray', 'server', 'content-type', 'retry-after',
        'x-amzn-errortype', 'x-amzn-trace-id', 'x-amzn-requestid',
        'x-amz-request-id', 'x-amz-id-2',
      ];
      for (var i = 0; i < keys.length; i++) {
        var v = resp.headers.get(keys[i]);
        if (v != null) headers[keys[i].replace(/-/g, '_')] = String(v).slice(0, 200);
      }
    }
    var props = {
      label: label,
      url_host: u ? u.host : null,
      url_path: u ? u.pathname : null,
      status: resp ? resp.status : null,
      ok: resp ? !!resp.ok : null,
      duration_ms: startedAt ? (Date.now() - startedAt) : null,
      body_success: (body && typeof body.success === 'boolean') ? body.success : null,
      body_error: (body && body.error) ? String(body.error).slice(0, 200) : null,
      body_phase: (body && body.phase) ? String(body.phase).slice(0, 80) : null,
      error_name: error ? error.name : null,
      error_message: error ? String(error.message || '').slice(0, 200) : null,
    };
    Object.assign(props, headers);
    if (extra) Object.assign(props, extra);
    track('network_call', props);
  } catch (e) {
    log('wcCaptureNetworkEvent failed (non-fatal)', e && e.message);
  }
}

// ─── Cart-attribute stitching (tracking spec §1.3) ────────────
// Stamps split-test identity onto the CART via one /cart/update.js call so it
// survives to checkout `attributes` and order `note_attributes` — the purchase
// join spine. The theme wrote ZERO cart attributes before this (verified
// 2026-07-01); without the stamp every wolden_purchase emits unstitched and
// per-arm revenue is unattributable
// (docs/specs/order-webhook-purchase-events.md §Handoff).
//
// WHY it lives here (not wc77-selectors.js): BOTH add-to-cart paths need it —
// the modal /cart/add.js path (wc77-selectors calls the export directly) AND
// the native PDP form path (current arm's hidden-properties form, flow-b,
// flow-c, and the standalone page all submit the same buy-buttons form — the
// document-level submit listener at the bottom of this file covers them all).
// This module is the one file every wc77 page already loads.
//
// Contract:
//  • Fire-and-forget, ONE retry, never throws, never blocks/awaits ATC.
//  • Idempotent per page load: an identical attribute set is not re-POSTed.
//    The cache is only set on a SUCCESSFUL write, so a failed write retries
//    naturally on the next ATC instead of being masked.
//  • Names are the spec §1.3 set — `_aid` specifically, because the droplet
//    connector (connector_shopify.extract_attributes) already canonicalizes
//    _aid → anonymous_id. The webhook dual-reads alias spellings as fallback.
//  • `_wc_flow` / `_wc_sub` are ALWAYS present (blank when unknown) so a
//    stale value from an earlier assignment state is cleared, not inherited;
//    the webhook's getNoteAttr treats '' as absent.
//  • The standalone watercolor page is OUTSIDE the test: `_wc_flow` is forced
//    to 'standalone' there (the visitor may still carry a test-arm cookie,
//    but a standalone purchase must never count toward an arm's revenue).
//  • WC_FLOW absent / errored / uid-less (assigner exception, kill switch on
//    before any assignment existed): mint a uid HERE and persist it in the
//    same `wc_ab` record the assigner reads — no arm/salt keys, so the
//    assigner adopts this uid and derives the arm from it on the next
//    pageview (identity continuity, never a fork).

var _lastCartAttrsPayload = null; // last SUCCESSFULLY written body (idempotence)
var _mintedUid = null;            // same-pageload fallback if persistence fails

function _readStoredUid() {
  try {
    var m = document.cookie.match(/(?:^|;\s*)wc_ab=([^;]+)/);
    if (m) { var c = JSON.parse(decodeURIComponent(m[1])); if (c && c.uid) return c.uid; }
  } catch (e) { /* ignore */ }
  try {
    var l = localStorage.getItem('wc_ab');
    if (l) { var s = JSON.parse(l); if (s && s.uid) return s.uid; }
  } catch (e) { /* ignore */ }
  return _mintedUid;
}

function _persistMintedUid(uid) {
  _mintedUid = uid;
  // Record shape + cookie attributes mirror snippets/wolden-flow-assign.liquid
  // (1yr, SameSite=Lax, domain-scoped only on the real storefront — a
  // mismatched Domain attribute makes the browser DROP the cookie on previews).
  var rec = JSON.stringify({ v: 1, uid: uid, at: Date.now() });
  try {
    var a = '; Max-Age=31536000; Path=/; SameSite=Lax';
    if (/(^|\.)woldenart\.com$/.test(location.hostname)) a += '; Domain=.woldenart.com';
    if (location.protocol === 'https:') a += '; Secure';
    document.cookie = 'wc_ab=' + encodeURIComponent(rec) + a;
  } catch (e) { /* ignore */ }
  try { localStorage.setItem('wc_ab', rec); } catch (e) { /* ignore */ }
}

export function wcWriteCartAttributes() {
  try {
    var f = null;
    try { f = window.WC_FLOW || null; } catch (e) { /* ignore */ }
    // Standalone watercolor page marker: the flow-b root rendered with
    // standalone:true carries data-wc-standalone (snippets/wolden-flow-b.liquid).
    var standalone = false;
    try { standalone = !!document.querySelector('[data-wc-standalone]'); } catch (e) { /* ignore */ }

    var uid = (f && f.uid) || _readStoredUid();
    if (!uid) { uid = uuid(); _persistMintedUid(uid); }

    var gen = null;
    try { gen = (window.WC && window.WC._currentResult && window.WC._currentResult.generationId) || null; } catch (e) { /* ignore */ }

    var ph = null, phSes = null;
    try {
      if (window.posthog) {
        if (typeof window.posthog.get_distinct_id === 'function') ph = window.posthog.get_distinct_id() || null;
        if (typeof window.posthog.get_session_id === 'function') phSes = window.posthog.get_session_id() || null;
      }
    } catch (e) { /* ignore — ad-blocked/pre-hydration PostHog just omits the key */ }

    // GA4 ids off the first-party cookies (spec §3.2): client_id = the part of
    // _ga after the 2nd dot; session_id from the GS2 (`.s<digits>`) or GS1
    // stream cookie for measurement id G-N79VPFKHK8.
    var gaClient = null, gaSession = null;
    try {
      var ga = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
      if (ga) { var p = decodeURIComponent(ga[1]).split('.'); if (p.length >= 4) gaClient = p.slice(2).join('.'); }
      var gs = document.cookie.match(/(?:^|;\s*)_ga_N79VPFKHK8=([^;]+)/);
      if (gs) { var gv = decodeURIComponent(gs[1]); var gm = gv.match(/\.s(\d+)/) || gv.match(/^GS\d\.\d\.(\d+)/); if (gm) gaSession = gm[1]; }
    } catch (e) { /* ignore */ }

    var attrs = {
      _aid: uid,
      _wc_flow: standalone ? 'standalone' : ((f && !f.error && f.arm) ? f.arm : ''),
      _wc_sub: (!standalone && f && f.subVariant) ? String(f.subVariant) : '',
    };
    if (gen) attrs._wc_gen_id = String(gen);
    if (ph) attrs._ph_distinct_id = String(ph);
    if (phSes) attrs._ph_session_id = String(phSes);
    if (gaClient) attrs._ga4_client_id = gaClient;
    if (gaSession) attrs._ga4_session_id = gaSession;

    var payload = JSON.stringify({ attributes: attrs });
    if (payload === _lastCartAttrsPayload) return Promise.resolve(true);

    // JSON POST to /cart/update.js — same shape as the live oai_oppref stamp
    // (layout/theme.liquid:9), which is first-hand proof the endpoint accepts
    // it. keepalive lets the request survive an immediate navigation. An
    // HTML content-type response = Cloudflare challenge → treated as failure
    // so the single retry (below) carries the clearance, mirroring
    // _cartAddWithRetry's rationale in wc77-selectors.js.
    var doPost = function () {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: payload,
        credentials: 'same-origin',
        keepalive: true,
      }).then(function (r) {
        var ct = ((r.headers && r.headers.get('Content-Type')) || '').toLowerCase();
        return !!(r.ok && ct.indexOf('text/html') !== 0);
      });
    };

    return doPost()['catch'](function () { return false; }).then(function (ok) {
      if (ok) { _lastCartAttrsPayload = payload; return true; }
      return new Promise(function (resolve) {
        setTimeout(function () {
          doPost().then(function (ok2) {
            if (ok2) _lastCartAttrsPayload = payload;
            resolve(!!ok2);
          })['catch'](function () { resolve(false); });
        }, 1200);
      });
    });
  } catch (e) {
    try { log('wcWriteCartAttributes failed (non-fatal)', e && e.message); } catch (e2) { /* ignore */ }
    return Promise.resolve(false);
  }
}

// ─── Classic-script bridge ────────────────────────────────────
// asset_url-loaded classic scripts (wc-atc-guard.js) can't import ES modules;
// WC.track hands them the same 3-leg fan-out. Safe to assign unconditionally:
// theme.liquid loads EITHER this v2 chain OR the legacy monolith (the only
// other WC.track definer) — never both. wcWriteCartAttributes rides along for
// the same reason (classic scripts / console QA can trigger a stamp).
try {
  window.WC = window.WC || {};
  window.WC.track = track;
  window.WC.wcWriteCartAttributes = wcWriteCartAttributes;
} catch (e) { /* ignore */ }

// ─── Call site B — native PDP form ATC (spec §2.1 step 6, "both paths") ──
// One document-level submit listener covers every arm: the current flow's
// hidden-properties form, flow-b, flow-c, and the standalone page all submit
// the same buy-buttons {% form 'product' %} (action /cart/add). CAPTURE phase
// because Horizon's ProductFormComponent.handleSubmit preventDefaults this
// same event and fetches /cart/add.js itself — capture guarantees we run
// first either way. Deliberately NOT scoped to the main buy-buttons form:
// these attributes are cart-level visitor identity (not line metadata), so an
// add from a recommended-product card should stamp them too. The pre-gen ATC
// guard (wc-atc-guard.js) preventDefaults the CLICK, which suppresses form
// submission entirely — blocked adds never reach here. Fire-and-forget: a
// failure can never interfere with the add.
try {
  if (!window.__wcCartAttrsSubmitWired) {
    window.__wcCartAttrsSubmitWired = true;
    document.addEventListener('submit', function (e) {
      try {
        var form = e.target;
        var action = (form && form.getAttribute && form.getAttribute('action')) || '';
        if (action.indexOf('/cart/add') === -1) return;
        wcWriteCartAttributes();
      } catch (err) { /* stitching is best-effort — never interfere with ATC */ }
    }, true);
  }
} catch (e) { /* ignore */ }
